import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { CotizacionCrearDTO } from '../../models/cotizador-contratante/crear cotizacion/cotizacion-crear-dto';
import { CotizacionContratanteDTO } from '../../models/cotizador-contratante/crear cotizacion/cotizacion-contratante-dto';
import { CotizacionContratanteValidacionDTO } from '../../models/cotizador-contratante/crear cotizacion/cotizacion-contratante-validacion-dto';
import { CrearCotizacionDesdePasoContratanteRequestDTO } from '../../models/cotizador-contratante/crear cotizacion/crear-cotizacion-desde-paso-contratante-request.dto';
import { CrearCotizacionDesdePasoContratanteResponseDTO } from '../../models/cotizador-contratante/crear cotizacion/crear-cotizacion-desde-paso-contratante-response.dto';
import { URL_MAP } from '../../shared/urlMap';
import { environment } from '../../../environments/environment';

/** Forma del payload en localStorage validaciones_contratante (items). */
interface ItemValidacionStorage {
  fuente?: string;
  descripcion?: string;
  fechaEvento?: string;
  tipo?: string;
  bloquea_proceso?: boolean;
}

/** Forma de datosRQ002_Contratante en localStorage. */
interface DatosContratanteStorage {
  tipoDocumento?: string;
  numeroDocumento?: string;
  primerNombre?: string;
  segundoNombre?: string;
  primerApellido?: string;
  segundoApellido?: string;
  genero?: string;
  fechaNacimiento?: string | Date;
  emailPrincipal?: string;
  emailSecundario?: string;
  telefono?: string;
  telefonoFijo?: string;
  edad?: number;
  esUsuario?: boolean;
  /** Id asesor logueado (venta nueva o inclusión), guardado al buscar contratante. */
  idAsesor?: string | null;
  /** 'S' si hay cotizaciones previas de otro asesor, 'N' si no. */
  flagExisteCotizacionOtroAsesor?: string;
  /** 'S' si hay contrato activo de otro asesor (venta nueva), 'N' si no. */
  flagExisteContratoOtroAsesor?: string;
}

/** Flags opcionales para el contratante (cotización/contrato otro asesor). */
export interface FlagsContratanteGuardado {
  flagExisteCotizacionOtroAsesor?: string;
  flagExisteContratoOtroAsesor?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CrearCotizacionContratanteService {

  private readonly apiUrl = `${environment.HOST}${URL_MAP.API.COTIZACION_DESDE_PASO_CONTRATANTE}`;

  // ─── Datos quemados para CotizacionCrearDTO (ajustar según catálogos backend) ───
  private readonly COTIZACION_ESTADO_INCOMPLETO = 1;
  private readonly PASO_ACTUAL_CONTRATANTE = 2;
  private readonly ID_PROBABILIDAD_CIERRE_DEFAULT = 3;

  /** Mapeo TIPO_PLAN (localStorage key "tipoPlan") → ID_TIPO_PLAN. */
  private readonly MAP_TIPO_PLAN_TO_ID: Record<string, number> = {
    'A': 1,   // ASOCIADO
    'F': 2,   // FAMILIAR
    'E': 3,   // EMPLEADO
    'C': 4,   // COLECTIVO
    'GA': 5   // GRUPOS DE AFINIDAD
  };

  constructor(private http: HttpClient) { }

  /**
   * Mapea el valor de localStorage "tipoPlan" (F, A, E, C, GA) al ID_TIPO_PLAN numérico.
   * Si no coincide, retorna 2 (FAMILIAR) por defecto.
   */
  mapTipoPlanToId(tipoPlan: string): number {
    const key = (tipoPlan ?? '').trim().toUpperCase();
    return this.MAP_TIPO_PLAN_TO_ID[key] ?? 2;
  }

  /**
   * Mapea tipoPlan al ID_TIPO_COTIZACION: C (Colectivo) → 2 (Colectivo Estándar), resto → 1 (Individual).
   */
  mapTipoPlanToIdTipoCotizacion(tipoPlan: string): number {
    const key = (tipoPlan ?? '').trim().toUpperCase();
    return key === 'C' ? 2 : 1;
  }

  /**
   * Construye CotizacionCrearDTO con datos quemados y campos dinámicos (idAsesor, tipoSolicitud, tipoPlan).
   * tipoSolicitud viene ya como "1","2","3" (no hay mapeo). tipoPlan se mapea con mapTipoPlanToId.
   */
  buildCotizacionCrearDTO(idAsesor: string, tipoSolicitud: string, tipoPlan: string): CotizacionCrearDTO {
    const idTipoSolicitudNum = Number(tipoSolicitud) || 1;
    return {
      idAsesor,
      estado: this.COTIZACION_ESTADO_INCOMPLETO,
      pasoActual: this.PASO_ACTUAL_CONTRATANTE,
      idTipoSolicitud: idTipoSolicitudNum,
      idTipoPlan: this.mapTipoPlanToId(tipoPlan),
      observaciones: undefined,
      idProbabilidadCierre: this.ID_PROBABILIDAD_CIERRE_DEFAULT,
      fechaCierre: undefined,
      idTipoCotizacion: this.mapTipoPlanToIdTipoCotizacion(tipoPlan)
    };
  }

  /**
   * Mapea datos del localStorage (datosRQ002_Contratante) a CotizacionContratanteDTO.
   */
  mapDatosContratanteToDTO(datos: DatosContratanteStorage): CotizacionContratanteDTO {
    const fechaNac = datos.fechaNacimiento;
    const fechaNacStr = fechaNac instanceof Date
      ? this.toISODateOnly(fechaNac)
      : (typeof fechaNac === 'string' ? this.normalizeDateString(fechaNac) : '');
    return {
      idContratante: undefined,
      idCotizacion: undefined,
      tipoDocumento: (datos.tipoDocumento ?? '').trim(),
      numDocumento: (datos.numeroDocumento ?? '').trim(),
      primerNombre: (datos.primerNombre ?? '').trim(),
      segundoNombre: (datos.segundoNombre ?? '').trim() || undefined,
      primerApellido: (datos.primerApellido ?? '').trim(),
      segundoApellido: (datos.segundoApellido ?? '').trim() || undefined,
      genero: (datos.genero ?? '').trim(),
      fechaNacimiento: fechaNacStr,
      edad: Number(datos.edad) || 0,
      correo: (datos.emailPrincipal ?? '').trim(),
      correoAdicional: (datos.emailSecundario ?? '').trim() || undefined,
      telefono: (datos.telefono ?? '').trim(),
      telefonoAdicional: (datos.telefonoFijo ?? '').trim() || undefined,
      esUsuario: datos.esUsuario === true ? 'S' : 'N',
      flagExisteCotizacionOtroAsesor: datos.flagExisteCotizacionOtroAsesor ?? undefined,
      flagExisteContratoOtroAsesor: datos.flagExisteContratoOtroAsesor ?? undefined
    };
  }

  /**
   * Mapea ítems de validaciones_contratante a CotizacionContratanteValidacionDTO[].
   */
  mapValidacionesToDTO(items: ItemValidacionStorage[]): CotizacionContratanteValidacionDTO[] {
    if (!Array.isArray(items)) return [];
    return items.map(item => ({
      idValidacion: undefined,
      idContratante: undefined,
      fuente: (item.fuente ?? '').trim(),
      tipoAlerta: (item.tipo ?? '').trim() || 'info',
      descripcion: (item.descripcion ?? '').trim(),
      fechaEvento: item.fechaEvento ? this.normalizeDateTimeString(item.fechaEvento) : undefined,
      severidad: this.mapTipoToSeveridad(item.tipo),
      bloqueaProceso: item.bloquea_proceso === true ? 'S' : 'N'
    }));
  }

  /**
   * Construye el request completo a partir de lo guardado en localStorage.
   * idAsesor y flags (flagExisteCotizacionOtroAsesor, flagExisteContratoOtroAsesor) se leen desde datosContratante (datosRQ002_Contratante).
   * tipoSolicitud: key "tipoSolicitud". tipoPlan: key "tipoPlan".
   */
  buildRequestFromStorage(
    datosContratante: DatosContratanteStorage,
    validacionesPayload: { items?: ItemValidacionStorage[] },
    tipoSolicitud: string,
    tipoPlan: string
  ): CrearCotizacionDesdePasoContratanteRequestDTO {
    const idAsesor = (datosContratante.idAsesor ?? '').toString().trim() || '';
    const cotizacion = this.buildCotizacionCrearDTO(idAsesor, tipoSolicitud, tipoPlan);
    const contratante = this.mapDatosContratanteToDTO(datosContratante);
    const validaciones = this.mapValidacionesToDTO(validacionesPayload?.items ?? []);
    const contratosInclusion = this.mapContratosInclusionFromStorage();
    return { cotizacion, contratante, validaciones, contratosInclusion };
  }

  public mapContratosInclusionFromStorage(): any[] {
    try {
      const raw = localStorage.getItem('contrato_radicado');
      if (raw) {
        const parsed = JSON.parse(raw);
        const contratos = Array.isArray(parsed.contrato) ? parsed.contrato : (parsed.contrato ? [parsed.contrato] : []);
        if (contratos.length > 0) {
          return contratos.map((c: any) => ({
            numeroContrato: (c.contra ?? c.numeroContrato ?? c.numero_contrato ?? '').toString().trim(),
            linea: (c.Linea_Negocio ?? c.linea ?? c.cod_linea_negocio ?? '').toString().trim(),
            plan: (c.Cuenta ?? c.plan ?? c.codigoTipoPlan ?? '').toString().trim(),
            programa: (c.cod_programa_afi ?? c.codigoPrograma ?? c.programa ?? '').toString().trim(),
            estadoMora: (c.mora ?? c.estadoMora ?? c.estado_mora ?? '').toString().trim(),
            asesorPoblacion: (c.nombreAsesor ?? c.nombre_asesor ?? c.asesorPoblacion ?? c.nombreAsesorPoblacion ?? '').toString().trim(),
            esSeleccionado: 'S'
          }));
        }
      }

      // Fallback para edición
      const rawEdicion = localStorage.getItem('contratos_inclusion_edicion');
      if (rawEdicion) {
        const contratosEdicion = JSON.parse(rawEdicion);
        if (Array.isArray(contratosEdicion)) {
          return contratosEdicion.map((c: any) => ({
            numeroContrato: (c.numeroContrato ?? c.numero_contrato ?? c.contra ?? '').toString().trim(),
            linea: (c.linea ?? c.Linea_Negocio ?? c.cod_linea_negocio ?? '').toString().trim(),
            plan: (c.plan ?? c.Cuenta ?? c.codigoTipoPlan ?? '').toString().trim(),
            programa: (c.programa ?? c.cod_programa_afi ?? c.codigoPrograma ?? '').toString().trim(),
            estadoMora: (c.estadoMora ?? c.mora ?? c.estado_mora ?? '').toString().trim(),
            asesorPoblacion: (c.asesorPoblacion ?? c.nombreAsesor ?? c.nombre_asesor ?? '').toString().trim(),
            esSeleccionado: c.esSeleccionado === 'S' || c.esSeleccionado === true ? 'S' : 'N'
          }));
        }
      }

      return [];
    } catch (e) {
      console.error('Error mapeando contratos de inclusión desde storage:', e);
      return [];
    }
  }

  crearDesdePasoContratante(body: CrearCotizacionDesdePasoContratanteRequestDTO): Observable<CrearCotizacionDesdePasoContratanteResponseDTO> {
    console.log('crearDesdePasoContratante – request completo:', JSON.stringify(body, null, 2));
    return this.http.post<CrearCotizacionDesdePasoContratanteResponseDTO>(this.apiUrl, body).pipe(
      catchError(error => {
        console.error('Error en crearDesdePasoContratante:', error);
        return throwError(() => new Error(error?.message || 'Error al crear cotización desde paso contratante'));
      })
    );
  }

  private toISODateOnly(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private normalizeDateString(s: string): string {
    const date = new Date(s);
    return isNaN(date.getTime()) ? '' : this.toISODateOnly(date);
  }

  private normalizeDateTimeString(s: string): string {
    const date = new Date(s);
    return isNaN(date.getTime()) ? '' : date.toISOString();
  }

  private mapTipoToSeveridad(tipo?: string): string {
    const t = (tipo ?? '').toLowerCase();
    if (t === 'error') return 'ALTA';
    if (t === 'success') return 'BAJA';
    return 'MEDIA';
  }
}
